import { Badge, Button, Divider, Tooltip, useComputedColorScheme } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDeepCompareMemo } from '@react-hookz/web';
import { IconCheck, IconLayoutBottombarCollapseFilled, IconLayoutNavbarCollapseFilled } from '@tabler/icons-react';
import Link from 'next/link';
import { useEffect, useState, useMemo } from 'react';
import { useAtom } from 'jotai';
import type { ArrayDiff } from '~/common/array/findDiffs';
import { findDiffs } from '~/common/array/findDiffs';
import type { DiffWindow, ExpandDirection, StepDifference } from '~/common/test/diffTypes';
import { compareOperations, compareSteps, operationListsAreEqual } from '~/common/test/step-comparer';
import { useProjectId } from '~/hooks/context/useProjectId';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { api } from '~/trpc-extra/api';
import type { TestRunExecutionOutput } from '~/types';
import { isAcceptingAutohealChangesAtom } from '~/atoms/atoms';
import StepDiffsList from './StepDiffsList';
import ScreenshotDiffViewer from './screenshots/ScreenshotDiffViewer';
import { openViaForm } from '~/logic/url/open-via-form';
import { useAcceptAutohealChanges } from '~/hooks/test/use-accept-autoheal-changes';

type TestContentSteps = {
  steps: SelectedStep[];
  testContentId: string;
};

export default function DiffViewer({
  testId,
  prevContent,
  newContent,
  prevStepsTitle,
  newStepsTitle,
  testRunOutput,
  diffWindowSize = 1, // number of steps to show before and after a diff
  expandSize = 2, // number of steps to expand the window by when moving up or down
  idsForNewStepsToShow,
  gitBranch,
  canAcceptChanges,
  testRunAttemptId,
  onAutohealAccepted,
}: {
  testId?: string;
  prevContent: TestContentSteps;
  newContent: TestContentSteps;
  prevStepsTitle?: React.ReactNode;
  newStepsTitle?: React.ReactNode;
  testRunOutput?: TestRunExecutionOutput;
  diffWindowSize?: number;
  expandSize?: number;
  idsForNewStepsToShow?: string[];
  gitBranch?: string;
  canAcceptChanges?: boolean;
  testRunAttemptId?: string;
  onAutohealAccepted?: () => Promise<void> | void;
}) {
  const colorScheme = useComputedColorScheme();
  const projectId = useProjectId();
  const { mutateAsync: editChanges, isPending: isEditingChanges } = api.testContent.fork.useMutation({
    onMutate: () => {
      notifications.show({ message: 'Creating a new draft copy from AI changes...', color: 'gray' });
    },
    onSuccess: (testDraft) => {
      openViaForm(`/project/${projectId}/testDraft/${testDraft.id}`, '_blank');
    },
    onError: (e) => {
      console.error(e);
      notifications.show({ message: 'Failed to create test draft', color: 'red' });
    },
  });

  // For showing autoheal changes, we use the input steps unless there are AI changes for that step
  // (Since if a test ends early, the output test content won't have the later steps, which we should show as unchanged.)
  const aiChangedNewSteps = useMemo(() => {
    if (!testRunOutput?.aiChanges?.length) {
      // No AI changes -> use newContent steps (compare prev steps against new steps)
      // (testRunOutput may not be passed to this component, in which case the user should simply see the diff between the prev and new steps. Eg. for version comparison.)
      return newContent.steps;
    }

    // Create a set of step indices that have AI changes (there will be a 1:1 mapping between steps)
    const stepsWithAiChangesIdxs = new Set(testRunOutput.aiChanges.map((change) => change.stepIndex));

    // Build the aiChangedNewSteps: use output step if it has AI changes, otherwise use input step
    const prevContentWithAiChanges = prevContent.steps.map((inputStep, index) =>
      stepsWithAiChangesIdxs.has(index) ?
        // This step has AI changes, use the corresponding output step
        newContent.steps[index] || inputStep
        // No AI changes for this step, use the input step
      : inputStep,
    );

    return prevContentWithAiChanges;
  }, [prevContent.steps, newContent.steps, testRunOutput?.aiChanges]);

  // Use specific loading state for this test content
  const [isAcceptingAutohealChanges] = useAtom(isAcceptingAutohealChangesAtom(newContent.testContentId));

  // TODO(https://app.dartai.com/my-tasks/0DwEvo7MRoBM-Make-accept-autoheal-changes)
  // change the acceptChanges logic to use the aiChangedNewSteps' steps
  const { acceptChanges, isPending: isAcceptingChanges } = useAcceptAutohealChanges({
    testContentId: newContent.testContentId,
    onSuccess: async () => {
      await onAutohealAccepted?.();
    },
  });

  const { data: test } = api.test.getTest.useQuery({ id: testId! }, { enabled: !!testId });
  const isAccepted = test?.trunkContentId && test?.trunkContentId === newContent.testContentId;

  const [hasDifferences, setHasDifferences] = useState(false);
  const [diffWindows, setDiffWindows] = useState<DiffWindow[]>([]);
  const [allExpanded, setAllExpanded] = useState<boolean | undefined>(); // undefined means the user has not interacted with the expand/collapse all button

  // useDeepCompareMemo is used to prevent unnecessary re-renders when the stepDifferences array is updated
  // (normal memoization does not work here because the stepDifferences array is a nested array)
  const stepDifferences: StepDifference[] = useDeepCompareMemo(
    () =>
      findDiffs(prevContent.steps, aiChangedNewSteps, compareSteps).map((stepDiff) => ({
        stepDiff,
        operationDiffs: getOperationDiffs(stepDiff),
      })),
    [prevContent.steps, aiChangedNewSteps],
  );

  // finds steps of interest and creates initial windows around each one, adjacent windows are then merged together
  // steps of interest are set to be expanded by default
  useEffect(() => {
    if (stepDifferences.length === 0) {
      return;
    }

    const idsForNewStepsToShowSet = new Set(idsForNewStepsToShow);
    const stepDifferencesWithIndices = stepDifferences.map((diff, idx) => ({
      ...diff,
      index: idx,
    }));

    const stepIdsWithErrors = new Set(testRunOutput?.errors?.map((error) => error.stepId) ?? []);

    const stepsToShow =
      idsForNewStepsToShow ?
        // if specific step IDs are provided, filter to show only those steps
        stepDifferencesWithIndices.filter(
          ({ stepDiff }) =>
            idsForNewStepsToShowSet.has(stepDiff.new?.id ?? '') || idsForNewStepsToShowSet.has(stepDiff.prev?.id ?? ''),
        )
        // otherwise, show steps that are different, have operation differences, or have errors
      : stepDifferencesWithIndices.filter(
          ({ stepDiff }) =>
            stepDiff.type !== 'unchanged' ||
            (stepDiff.prev &&
              stepDiff.new &&
              !operationListsAreEqual(stepDiff.prev.operations, stepDiff.new.operations)) ||
            stepIdsWithErrors.has(stepDiff.new?.id ?? '') ||
            stepIdsWithErrors.has(stepDiff.prev?.id ?? ''),
        );

    // create initial expanded rows and windows
    const mergedWindows: DiffWindow[] = [];

    if (stepsToShow.length > 0) {
      setHasDifferences(true);

      for (const { index } of stepsToShow) {
        const newWindow: DiffWindow = {
          startIdx: Math.max(index - diffWindowSize, 0),
          endIdx: Math.min(index + diffWindowSize, stepDifferences.length - 1),
        };

        // merge previous window with new window if adjacent
        if (mergedWindows.length > 0) {
          const prevWindow = mergedWindows.at(-1);
          if (prevWindow && prevWindow.endIdx + 1 >= newWindow.startIdx) {
            mergedWindows[mergedWindows.length - 1] = {
              ...prevWindow,
              endIdx: newWindow.endIdx,
            };
          } else {
            mergedWindows.push(newWindow);
          }
        } else {
          mergedWindows.push(newWindow);
        }
      }
    } else {
      // Even though there's no step differences, the AI might have healed some steps
      // so we cannot say there were no differences between the two tests
      if ((testRunOutput?.aiChanges?.length ?? 0) > 0) {
        setHasDifferences(true);
      }
      // if no differences are found, show all steps
      mergedWindows.push({
        startIdx: 0,
        endIdx: stepDifferences.length - 1,
      });
    }

    setDiffWindows(mergedWindows);
  }, [stepDifferences, diffWindowSize, testRunOutput?.errors]);

  function getOperationDiffs(stepDifference: ArrayDiff<SelectedStep>) {
    return stepDifference.prev && stepDifference.new ?
        findDiffs(stepDifference.prev.operations, stepDifference.new.operations, compareOperations)
      : [];
  }

  function updateDiffWindow(diffWindowIdx: number, direction: ExpandDirection) {
    const newWindows = [...diffWindows];
    const { startIdx, endIdx } = newWindows[diffWindowIdx];

    newWindows[diffWindowIdx] = {
      ...newWindows[diffWindowIdx],
      startIdx: Math.max(direction === 'up' ? startIdx - expandSize : startIdx, 0),
      endIdx: Math.min(direction === 'down' ? endIdx + expandSize : endIdx, stepDifferences.length - 1),
    };

    // merge updated window with previous window if adjacent
    // windows can never be undefined since we remove them once merged
    if (
      direction === 'up' &&
      diffWindowIdx > 0 &&
      newWindows[diffWindowIdx - 1].endIdx + 1 >= newWindows[diffWindowIdx].startIdx
    ) {
      newWindows[diffWindowIdx - 1] = {
        ...newWindows[diffWindowIdx - 1],
        endIdx: newWindows[diffWindowIdx].endIdx,
      };
      newWindows.splice(diffWindowIdx, 1);
    } else if (
      direction === 'down' &&
      diffWindowIdx < newWindows.length - 1 &&
      newWindows[diffWindowIdx].endIdx + 1 >= newWindows[diffWindowIdx + 1].startIdx
    ) {
      newWindows[diffWindowIdx] = {
        ...newWindows[diffWindowIdx],
        endIdx: newWindows[diffWindowIdx + 1].endIdx,
      };
      newWindows.splice(diffWindowIdx + 1, 1);
    }

    setDiffWindows(newWindows);
  }

  const acceptDisabled =
    !hasDifferences || isEditingChanges || isAcceptingChanges || isAcceptingAutohealChanges || !test?.id;

  return (
    <div className="relative flex h-full flex-col gap-4">
      <ScreenshotDiffViewer />
      <div className="flex flex-col gap-4">
        {/* Header for the diff viewer */}
        <div className="ml-8 flex items-center gap-4 pt-4">
          <div className="flex flex-1 gap-4">
            {!!prevStepsTitle && (
              <div className="flex items-center gap-2">
                {prevStepsTitle}
                <Divider
                  orientation="vertical"
                  className="my-1 ml-1"
                />
              </div>
            )}
            <Tooltip label={allExpanded ? 'Collapse all steps' : 'Expand all steps'}>
              <Button
                variant="default"
                color={colorScheme === 'dark' ? 'gray.4' : 'gray.7'}
                size="compact-sm"
                onClick={() => {
                  if (!allExpanded) {
                    setDiffWindows([
                      {
                        startIdx: 0,
                        endIdx: stepDifferences.length - 1,
                      },
                    ]);
                  }
                  setAllExpanded((prev) => !prev);
                }}
                className="mr-6"
                leftSection={
                  allExpanded ?
                    <IconLayoutBottombarCollapseFilled size={16} />
                  : <IconLayoutNavbarCollapseFilled size={16} />
                }
              >
                {allExpanded ? 'Collapse All' : 'Expand All'}
              </Button>
            </Tooltip>
          </div>

          <div className="flex-1">
            <div className="ml-3 flex justify-between gap-2 text-sm">
              <div className="flex items-center gap-2">
                {!!newStepsTitle && newStepsTitle}
                {!hasDifferences && (
                  <Badge
                    color="red"
                    variant="light"
                    className="ml-2"
                  >
                    No diffs found
                  </Badge>
                )}
              </div>
              {!!canAcceptChanges && (
                <div className="flex items-center gap-2">
                  {isAccepted ?
                    <Tooltip
                      label="View the updated test"
                      openDelay={250}
                    >
                      <Button
                        color="green"
                        variant="light"
                        leftSection={<IconCheck size={14} />}
                        component={Link}
                        href={`/project/${projectId}/test/${test?.id}`}
                        disabled={!test?.id}
                      >
                        Changes Accepted
                      </Button>
                    </Tooltip>
                  : <Tooltip
                      label="Accept the changes made by AI"
                      openDelay={250}
                    >
                      <Button
                        variant="default"
                        loading={isAcceptingChanges || isAcceptingAutohealChanges}
                        disabled={acceptDisabled}
                        onClick={() => {
                          void acceptChanges();
                        }}
                      >
                        Accept Changes
                      </Button>
                    </Tooltip>
                  }
                  <Tooltip
                    label="Create a new draft from the changes made by AI"
                    openDelay={250}
                  >
                    <Button
                      variant="default"
                      loading={isEditingChanges}
                      disabled={acceptDisabled}
                      onClick={() => {
                        void editChanges({
                          testContentId: newContent.testContentId,
                          gitBranch: gitBranch ?? undefined,
                        });
                      }}
                    >
                      Edit Changes
                    </Button>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </div>
        <StepDiffsList
          stepDifferences={stepDifferences}
          diffWindows={diffWindows}
          aiChanges={testRunOutput?.aiChanges ?? []}
          updateDiffWindow={updateDiffWindow}
          allExpanded={allExpanded}
          testRunAttemptId={testRunAttemptId}
          aiActionLogs={testRunOutput?.aiActionLogs ?? []}
        />
      </div>
    </div>
  );
}
